import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { sessionStore } from "./store";
import { IAccountContext } from "./types";
import { Loader } from "@mantine/core";
import { useRouter } from "next/router";
import {
  persistUTMQueryParamsToString,
  redirectAfterLogout,
} from "@common/utils/router";
import { paymentMethodsStore } from "@stores/payment-methods";

export const AccountContext = createContext<IAccountContext>({});

export function AccountProvider({ children }: PropsWithChildren): ReactElement {
  const router = useRouter();

  // payment
  const {
    isLoading: loadingDefaultPaymentMethod,
    refetch: refetchDefaultPaymentMethod,
    data: defaultPaymentMethod,
  } = paymentMethodsStore.useDefaultPaymentMethod({ enabled: false });

  // account
  const {
    isLoading: loadingAccount,
    error: accountError,
    refetch: refetchAccount,
    data: account,
  } = sessionStore.useGetAccount({
    enabled: true,
    refetchInterval: 1000 * 60 * 5, // reload every 5 minutes
  });

  const shouldLogoutCustomer = useMemo(
    () => !loadingAccount && !!account && accountError?.status === 404,
    [loadingAccount, account, accountError],
  );

  // first account loading
  const [firstLoading, setFirstLoading] = useState(true);
  useEffect(() => {
    async function getDetailsAsync() {
      await refetchAccount();
      await refetchDefaultPaymentMethod();

      setFirstLoading(false);
    }

    getDetailsAsync();
  }, [refetchAccount, refetchDefaultPaymentMethod]);

  // utils
  const consolidatedLoading = loadingAccount || loadingDefaultPaymentMethod;

  // redirects
  useEffect(() => {
    const { pathname, query } = router;

    // Redirecting because
    // the customer has been logged out
    // in the background
    if (shouldLogoutCustomer) {
      redirectAfterLogout(router, true);
    }
    // Customer is logged out
    else if (!consolidatedLoading) {
      const isLanding = pathname === "/";
      const isCheckout =
        pathname.includes("/checkout") &&
        !pathname.includes("/checkout/upgrade/self-hosted");
      const isAccount = pathname.includes("/account");
      const isSnapshots = pathname.includes("/snapshots/");

      if (isLanding || isCheckout || isAccount || isSnapshots) {
        const isLoggedIn = !!account;

        // 1. login
        // redirect to /account
        // redirectTo query parameter
        if (isLoggedIn && isLanding) {
          const { redirectTo, ...queryParams } = query;
          router.push(
            persistUTMQueryParamsToString(
              router,
              decodeURIComponent(redirectTo ? String(redirectTo) : "/account"),
              queryParams,
            ),
          );
        }
        // 2. checkout
        // redirect to /account/new-instance
        else if (isLoggedIn && defaultPaymentMethod && isCheckout) {
          const { redirectTo, ...queryParams } = query;
          router.push(
            persistUTMQueryParamsToString(
              router,
              decodeURIComponent(
                redirectTo ? String(redirectTo) : "/account/new-instance",
              ),
              queryParams,
            ),
          );
        }
        // 3. logout
        // redirect to /
        else if (!isLanding && !isCheckout && !isLoggedIn) {
          redirectAfterLogout(router);
        }
      }
    }
  }, [
    shouldLogoutCustomer,
    consolidatedLoading,
    router,
    account,
    defaultPaymentMethod,
  ]);

  if (consolidatedLoading && firstLoading) {
    return <Loader m="xl" />;
  }

  return (
    <AccountContext.Provider value={{ account }}>
      {children}
    </AccountContext.Provider>
  );
}
